// ── SwapQuoteCard ────────────────────────────────────────────────
//
// A small, read-only "how much would I get" estimate for buying a CashToken
// with BCH on Cauldron, plus a one-tap hand-off to the Cauldron swap page.
//
// IMPORTANT: this NEVER builds or signs a swap. It fetches pool reserves from
// the Cauldron indexer, runs a constant-product estimate (src/lib/cauldron.ts),
// and labels the result as an estimate. Execution happens on Cauldron's own
// audited frontend — we only deep-link to it. This keeps the app's security
// surface unchanged while making swaps feel integrated.

import { useState, useEffect, useMemo } from 'react';
import { ArrowUpRight, Loader2, Info } from 'lucide-react';
import {
  fetchPoolsForToken, estimateUsdToToken, cauldronSwapUrl, formatPricePerToken,
  type CauldronPool,
} from '../lib/cauldron';
import { getBCHPrice } from '../hooks/useBCHPrice';

interface SwapQuoteCardProps {
  tokenId: string;
  symbol: string;
  decimals: number;
  priceUsd: number | null;
  iconUrl?: string;
  /** Default USD amount to quote. */
  defaultUsd?: number;
  /** Compact variant for tight spaces (e.g. the tip picker nudge). */
  compact?: boolean;
}

const USD_PRESETS = [1, 5, 10, 25];

export default function SwapQuoteCard({
  tokenId, symbol, decimals, priceUsd, iconUrl, defaultUsd = 5, compact = false,
}: SwapQuoteCardProps) {
  const [pools, setPools] = useState<CauldronPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [usd, setUsd] = useState<number>(defaultUsd);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPoolsForToken(tokenId)
      .then(p => { if (alive) { setPools(p); setLoading(false); } })
      .catch(() => { if (alive) { setPools([]); setLoading(false); } });
    return () => { alive = false; };
  }, [tokenId]);

  const bchPrice = getBCHPrice();

  const quote = useMemo(() => {
    if (!bchPrice) return null;
    return estimateUsdToToken(usd, bchPrice, pools, decimals);
  }, [usd, bchPrice, pools, decimals]);

  const swapUrl = cauldronSwapUrl(tokenId);

  const outStr = quote && !quote.insufficient
    ? quote.outWhole.toLocaleString(undefined, { maximumFractionDigits: decimals === 0 ? 0 : Math.min(decimals, 4) })
    : null;

  const impactPct = quote ? quote.priceImpact * 100 : 0;
  const highImpact = impactPct >= 5;

  return (
    <div className={`rounded-xl border border-border bg-muted/30 ${compact ? 'p-2.5' : 'p-3'} space-y-2`}>
      <div className="flex items-center gap-2">
        {iconUrl && <img src={iconUrl} alt="" className="w-4 h-4 rounded-full" loading="lazy" />}
        <span className={`font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>Get {symbol}</span>
        {priceUsd != null && (
          <span className="text-[10px] text-muted-foreground ml-auto font-mono">
            {formatPricePerToken(priceUsd)}
          </span>
        )}
      </div>

      {/* USD amount presets */}
      <div className="flex gap-1">
        {USD_PRESETS.map(v => (
          <button
            key={v}
            onClick={() => setUsd(v)}
            className={`flex-1 py-1 rounded-md text-[11px] font-semibold font-mono transition-colors ${
              usd === v ? 'bg-emerald-500 text-white' : 'bg-background text-muted-foreground hover:text-foreground'
            }`}
          >
            ${v}
          </button>
        ))}
      </div>

      {/* Estimate */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">You'd get ≈</span>
        <span className="font-mono font-semibold text-foreground">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
            : !bchPrice ? '—'
            : outStr ? `${outStr} ${symbol}` : 'No liquidity'}
        </span>
      </div>

      {quote && !quote.insufficient && highImpact && (
        <p className="text-[10px] text-amber-500 flex items-start gap-1 leading-tight">
          <Info className="w-3 h-3 shrink-0 mt-px" />
          High price impact (~{impactPct.toFixed(0)}%) — try a smaller amount.
        </p>
      )}

      <p className="text-[9px] text-muted-foreground leading-tight">
        Estimate only. The exact rate is set when you swap on Cauldron.
      </p>

      <a
        href={swapUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="no-navigate flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors"
      >
        Swap on Cauldron <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}
